import { createContext, useContext } from "react"

import type { UseSearchStateReturn } from "@hooks/useSearchState"

import type { SelectListItem } from "@molecules/Select/SelectListItem/SelectListItem.types"

import { SelectionMode } from "./Select.types"

export interface SelectSearchData<T = unknown> {
    results: T[]
    isLoading: boolean
    isFetching: boolean
    isFetchingNextPage: boolean
    isError: boolean
    fetchNextPage: () => Promise<unknown>
    hasNextPage: boolean
    searchState: UseSearchStateReturn
}

export interface SelectContextValues {
    mode: "dialog" | "popover"
    selectionMode: SelectionMode
    selectedItem: SelectListItem | null
    setSelectedItem: (item: SelectListItem | null) => void
    syncSubtextToSuffix?: boolean
    customValue?: string
    setCustomValue?: (value: string) => void
    isOpen?: boolean
    setIsOpen?: (isOpen: boolean) => void
    searchData?: SelectSearchData<unknown>
}

export const SelectContext = createContext<SelectContextValues | undefined>(undefined)

SelectContext.displayName = "SelectContext"

export function useSelectContext() {
    const context = useContext(SelectContext)
    if (!context) {
        throw new Error("useSelectContext must be used within a SelectProvider")
    }
    return context
}

export function useSelectSearchData<T = unknown>() {
    const { searchData } = useSelectContext()

    if (!searchData) {
        throw new Error("useSelectSearchData must be used within a Select.Root with endpoint data")
    }

    return searchData as SelectSearchData<T>
}
